import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CreditCard, ExternalLink } from 'lucide-react';
import { getOpleiderStripeStatus, createOpleiderStripeOnboardingLink, createOpleiderStripeDashboardLink } from '../../services/opleidingenApi';

/**
 * Stripe Connect Express-koppeling van de opleider. Academy-boekingen (werkgeversdeel
 * via de verzamelfactuur + privé-bijbetaling van de medewerker) worden als één
 * transfer (netto 85%) naar dit account uitbetaald; zonder geverifieerde koppeling
 * blijft de uitbetaling openstaan. Het account hangt aan de hoofdeigenaar.
 */
export const OpleiderStripeKaart: React.FC<{ alleenLezen?: boolean }> = ({ alleenLezen = false }) => {
  const { t } = useTranslation('pages');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const { data, refetch } = useQuery({ queryKey: ['opleider-stripe-status'], queryFn: getOpleiderStripeStatus });
  const status = data?.onboarding_status ?? 'not_started';

  const foutmelding = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

  const koppel = async () => {
    setBezig(true); setFout(null);
    try {
      const { onboarding_url } = await createOpleiderStripeOnboardingLink();
      await refetch();
      if (onboarding_url) window.open(onboarding_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setFout(foutmelding(err, t('opleider.stripeLinkFailed')));
    } finally {
      setBezig(false);
    }
  };

  const openDashboard = async () => {
    setBezig(true); setFout(null);
    try {
      const { dashboard_url } = await createOpleiderStripeDashboardLink();
      if (dashboard_url) window.open(dashboard_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setFout(foutmelding(err, t('opleider.stripeDashboardFailed')));
    } finally {
      setBezig(false);
    }
  };

  const badge: Record<string, string> = {
    verified: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-700',
    not_started: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className={`p-4 rounded-xl border flex flex-wrap items-center justify-between gap-3 ${status === 'verified' ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-200'}`}>
      <div className="flex items-start gap-3">
        <CreditCard className="w-5 h-5 text-[#0B1F3A] flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-gray-900 flex items-center gap-2">
            {t('opleider.stripeTitle')}
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge[status] ?? badge.not_started}`}>
              {t(`opleider.stripeStatus_${status}`)}
            </span>
          </p>
          <p className="text-sm text-gray-600 mt-0.5">{t('opleider.stripeHint')}</p>
          {fout && <p className="text-sm text-red-600 mt-1" role="alert">{fout}</p>}
        </div>
      </div>
      {alleenLezen ? null : status === 'verified' ? (
        <button type="button" onClick={openDashboard} disabled={bezig}
          className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60">
          <ExternalLink className="w-4 h-4" />{t('opleider.stripeDashboard')}
        </button>
      ) : (
        <button type="button" onClick={koppel} disabled={bezig}
          className="flex items-center gap-2 bg-[#0B1F3A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3459] disabled:opacity-60">
          <CreditCard className="w-4 h-4" />
          {status === 'not_started' ? t('opleider.stripeConnect') : t('opleider.stripeContinue')}
        </button>
      )}
    </div>
  );
};
